/**
 * Step 12 — Profile data (combined screen) — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 3 step 12
 *
 * Synthesizes — this is the densest synthesis on the project. Three sources
 * converge into one screen with a personalized headline, progressive
 * reveal, and a sticky continue.
 *
 * What synthesis lifts:
 *   - Professional's components: Dropdown (custom keyboard-nav select),
 *     Toggle, Chip with 40ms-delayed checkmark.
 *   - Professional's TRANSLATION_LABEL hierarchy + Dropdown keyboard nav.
 *   - Experimental's progressive-reveal-with-summary-chips pattern: only
 *     the active section renders fully; completed sections collapse to
 *     a "Role · Growing Christian (edit)" summary chip. Saves vertical
 *     space, gives a sense of progress.
 *   - Experimental's TRANSLATION_META as the dropdown subtext per option.
 *   - Basic's sticky Continue at the bottom (always visible on long forms).
 *
 * What synthesis adds (Mason's lock):
 *   - NEW: Personalized "Welcome, {firstName}" header (Hulick / Mason). Uses
 *     OAuth-sourced first_name from step 1 onward.
 *   - Sticky Continue counts required-completed as "2 of 3 needed" until
 *     all three are set, then becomes the live "Continue" CTA.
 *
 * What synthesis drops:
 *   - "Step N of 4" eyebrows inside section headers (Mason banned the
 *     "Step N of 18" pattern; same logic — these were inherited from the
 *     experimental version, retired here).
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// Bible translation copyright / source hints — surface context per dropdown
// option so the user knows what they're picking.
const TRANSLATION_META = {
  KJV: 'Public domain · classic',
  NKJV: 'Modern · faithful to KJV',
  NASB: 'Word-for-word · scholarly',
  NLT: 'Thought-for-thought · readable',
  NIV: 'Balanced · most popular',
  ESV: 'Word-for-word · modern',
  WEB: 'Public domain · contemporary',
  YLT: 'Literal · academic',
}

/* ----------------------------- Custom dropdown ---------------------------- */
// Module-scope per static-components lint rule. Supports keyboard nav
// (up/down/enter/escape), click-outside close, and a meta line per option
// (used for translation context like "Word-for-word · scholarly").
function Dropdown({ label, value, placeholder, options, onSelect, optionMeta }) {
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 })
  const rootRef = useRef(null)
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  // Normalize options: each entry has { value, label }
  const normalized = options.map(o => (typeof o === 'string' ? { value: o, label: o } : o))
  const selected = normalized.find(o => o.value === value)

  // Position the menu via fixed coords (Portal target = document.body, so we
  // can't rely on parent positioning). Uses getBoundingClientRect on the
  // trigger button. useLayoutEffect runs synchronously after layout so the
  // menu doesn't flash at (0,0) on first paint.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return
    const r = buttonRef.current.getBoundingClientRect()
    setMenuPos({ top: r.bottom + 6, left: r.left, width: r.width })
  }, [open])

  useEffect(() => {
    function onDocClick(e) {
      // Click is "outside" if it's not in the trigger root AND not in the
      // portaled menu. Either counts as inside the dropdown surface.
      const inRoot = rootRef.current?.contains(e.target)
      const inMenu = menuRef.current?.contains(e.target)
      if (!inRoot && !inMenu) setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    function onScroll() {
      // Close only on PAGE scroll, not on internal menu scroll. capture:false
      // means we only fire when the event reaches window (scroll doesn't bubble),
      // so scrolling the dropdown's <ul> won't propagate up and close it.
      // (Bug 2026-05-14: capture:true was eating internal scrolls.)
      setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', onDocClick)
      document.addEventListener('keydown', onKey)
      window.addEventListener('scroll', onScroll, false)
      const idx = normalized.findIndex(o => o.value === value)
      setHighlight(idx >= 0 ? idx : 0)
    }
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, false)
    }
  }, [open, value, normalized])

  function handleKeyDown(e) {
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault()
      setOpen(true)
      return
    }
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(normalized.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = normalized[highlight]
      if (opt) {
        onSelect(opt.value)
        setOpen(false)
      }
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <label className="block text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted-soft)] mb-2.5">
        {label}
      </label>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="group w-full flex items-center justify-between px-4 py-3 bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-md text-left transition-colors duration-200 hover:border-[var(--gold-dim)] focus:outline-none focus-visible:border-[var(--gold)] focus-visible:ring-2 focus-visible:ring-[var(--gold)]/40"
      >
        <span
          className={
            selected
              ? 'text-[0.9375rem] text-[var(--text-primary)]'
              : 'text-[0.9375rem] text-[var(--text-muted-soft)]'
          }
        >
          {selected ? selected.label : placeholder}
        </span>
        <span
          aria-hidden="true"
          className="text-[var(--text-muted-soft)] transition-transform duration-200"
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transitionTimingFunction: EASE,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            className="pd-syn-menu dark-scrollbar fixed z-[1000] max-h-64 overflow-y-auto bg-[var(--bg-surface-high)] border border-[var(--glass-border)] rounded-md shadow-xl py-1"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              animation: `pdSynFadeIn 180ms ${EASE}`,
            }}
          >
            {normalized.map((opt, idx) => {
              const isSel = opt.value === value
              const isHi = idx === highlight
              const meta = optionMeta ? optionMeta[opt.value] : null
              // Hover styling lives in CSS (.pd-syn-menu li:hover) so real
              // pointer hover lights up regardless of whether React's synthetic
              // onMouseEnter fires through the portal. JS `isHi` state still
              // drives keyboard-nav highlighting via .is-kb class, and `isSel`
              // gives the currently-picked option a subtle gold tint at rest.
              // Mason flag 2026-05-14: prior JS-only hover was unreliable.
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={e => {
                    e.preventDefault()
                    onSelect(opt.value)
                    setOpen(false)
                  }}
                  className={`pd-syn-menu-item flex items-center justify-between px-4 py-2.5 cursor-pointer text-[0.9375rem] transition-colors duration-150${isSel ? ' is-selected' : ''}${isHi ? ' is-kb' : ''}`}
                >
                  <span className="flex flex-col">
                    <span>{opt.label}</span>
                    {meta && (
                      <span className="text-[10px] text-[var(--text-muted-soft)] leading-tight">
                        {meta}
                      </span>
                    )}
                  </span>
                  {isSel && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path
                        d="M3 7.5L5.5 10L11 4"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </li>
              )
            })}
          </ul>,
          document.body
        )}
    </div>
  )
}

/* ---------------------------------- Chip --------------------------------- */
// Module-scope per static-components lint rule.
// Hover state brightens border + text on unselected chips so the cursor
// has visible feedback before commit. Selected chips already glow gold
// so they don't need a separate hover treatment.
function Chip({ label, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={`pd-syn-chip group relative inline-flex items-center justify-center px-3.5 py-2 rounded-full border text-[0.8125rem] leading-none transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)]/40 ${selected ? 'pd-syn-chip--on' : 'pd-syn-chip--off'}`}
      style={{
        backgroundColor: selected ? 'var(--gold-select)' : 'var(--bg-surface)',
        borderColor: selected ? 'var(--gold)' : 'var(--glass-border)',
        color: selected ? 'var(--gold)' : 'var(--text-muted)',
        transitionTimingFunction: EASE,
      }}
    >
      {/* Check slot collapses width AND right-margin together so the label
          stays optically centered when nothing is selected. (Was using
          `gap-1.5` which left a permanent 6px gap even with the check
          collapsed — pushed the label visibly right.) */}
      <span
        aria-hidden="true"
        className="inline-flex overflow-hidden"
        style={{
          width: selected ? 14 : 0,
          marginRight: selected ? 6 : 0,
          opacity: selected ? 1 : 0,
          transition: `width 220ms ${EASE} 40ms, margin-right 220ms ${EASE} 40ms, opacity 180ms ${EASE} 40ms`,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M3 7.5L5.5 10L11 4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span>{label}</span>
    </button>
  )
}

/* --------------------------------- Toggle -------------------------------- */
function Toggle({ checked, onChange, id }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-[22px] w-[38px] flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)]/40"
      style={{
        backgroundColor: checked ? 'var(--gold)' : 'var(--glass-border)',
        transitionTimingFunction: EASE,
      }}
    >
      <span
        aria-hidden="true"
        className="absolute top-[3px] h-4 w-4 rounded-full bg-white shadow-sm"
        style={{
          left: checked ? 19 : 3,
          transition: `left 220ms ${EASE}`,
        }}
      />
    </button>
  )
}

/* ------------------------------ SummaryChip ------------------------------ */
// A completed section collapses to this slim chip. Tap to re-edit. Hover
// adds a subtle scale + gold glow (matches the section card hover treatment
// + the step 6 verse card pattern Mason locked in).
function SummaryChip({ label, value, onTap }) {
  return (
    <button
      type="button"
      onClick={onTap}
      className="pd-syn-summary w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-[var(--glass-border)] bg-[var(--bg-surface)] text-left"
    >
      <span className="flex items-center gap-3 min-w-0">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] shrink-0">
          {label}
        </span>
        <span className="font-heading text-base text-[var(--text-primary)] truncate">{value}</span>
      </span>
      <span className="text-xs text-[var(--gold)] shrink-0">edit</span>
    </button>
  )
}

/* ------------------------------- CloseIcon ------------------------------- */
// Module-scope close icon (lint static-components rule).
function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

/* ------------------------------ SectionCard ------------------------------ */
// Wraps an active section with the hover effect + a close button. The close
// button is dim by default and brightens on parent hover, so the affordance
// is discoverable without dominating. Mason's lock (2026-05-13).
function SectionCard({ children, onClose }) {
  return (
    <section className="pd-syn-section relative rounded-xl border border-[var(--gold-dim)] bg-[var(--bg-surface)] p-5 pd-syn-reveal">
      {onClose ? (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onClose()
          }}
          aria-label="Collapse this section"
          className="pd-syn-close absolute top-3 right-3 inline-flex items-center justify-center w-7 h-7 rounded-full"
        >
          <CloseIcon />
        </button>
      ) : null}
      {children}
    </section>
  )
}

/* -------------------------------- Rhythm --------------------------------- */
// 3 stacked sliders for age + days/wk + min/day. Replaces the standalone
// old step 3 (reading-rhythm) which got merged into this screen 2026-05-16.
// Defaults render sensible values so the user doesn't HAVE to interact —
// they can accept the defaults and move on. onChange fires with each drag.
function RhythmSliders({ age, days, minutes, onChange }) {
  const ageVal = age ?? 21
  const daysVal = days ?? 5
  const minVal = minutes ?? 10
  return (
    <div className="space-y-5">
      <SliderRow
        id="rhythm-age"
        label="Your age"
        value={ageVal}
        min={13}
        max={99}
        format={v => `${v} years`}
        onChange={v => onChange({ age: v })}
      />
      <SliderRow
        id="rhythm-days"
        label="Days you open the Bible"
        value={daysVal}
        min={0}
        max={7}
        format={v =>
          v === 0 ? 'Never' : v === 7 ? 'Daily' : `${v} ${v === 1 ? 'day' : 'days'} / wk`
        }
        onChange={v => onChange({ bible_reading_days_per_week: v })}
      />
      <SliderRow
        id="rhythm-minutes"
        label="Minutes per session"
        value={minVal}
        min={5}
        max={60}
        step={5}
        format={v => `${v} min`}
        onChange={v => onChange({ daily_time_goal: v })}
      />
    </div>
  )
}

// Module-scope SliderRow per lint static-components rule. Single labeled
// slider with gold fill, browser-consistent thumb via the global CSS at the
// bottom of this file (pd-syn-slider).
function SliderRow({ id, label, value, min, max, step = 1, format, onChange }) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-2">
        <label
          htmlFor={id}
          className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted-soft)] font-semibold"
        >
          {label}
        </label>
        <span
          className="font-heading text-[var(--text-primary)] text-[15px] tabular-nums"
          aria-live="polite"
        >
          {format ? format(value) : value}
        </span>
      </div>
      <div className="relative h-7 flex items-center">
        <div
          className="absolute inset-x-0 h-[3px] rounded-full"
          style={{ background: 'var(--bg-surface-high)' }}
        />
        <div
          className="absolute h-[3px] rounded-full pointer-events-none transition-[width] duration-150 ease-out"
          style={{
            width: `${pct}%`,
            background: 'linear-gradient(90deg, var(--gold-dim) 0%, var(--gold) 100%)',
          }}
        />
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="pd-syn-slider absolute inset-0 w-full h-full appearance-none bg-transparent cursor-pointer focus:outline-none"
          aria-label={label}
        />
      </div>
    </div>
  )
}

/* ============================== Main component ============================ */
export default function ProfileDataSynthesis({
  role,
  denomination,
  preferredTranslation,
  personalizeToDenomination,
  interests,
  age,
  days,
  minutes,
  onChange,
  onContinue,
  roleOptions,
  denominationOptions,
  translationOptions,
  interestsOptions,
  firstName,
  displayName,
}) {
  // Track which section the user is actively editing. Default: section
  // opens itself when the prior one fills in. Tapping a SummaryChip re-opens
  // a completed section.
  const [editing, setEditing] = useState(null) // null | 'name' | 'role' | 'denom' | 'trans' | 'interests'

  // Interests is optional. Mason's call (2026-05-13): user can collapse it
  // explicitly via the × close button. When collapsed, render as a summary
  // chip showing the current count (or "None"). Tap chip to re-expand.
  const [interestsCollapsed, setInterestsCollapsed] = useState(false)

  // Name section — Mason 2026-05-17: only required when displayName is
  // empty (i.e., the trading card would otherwise read "Welcome, friend.").
  // OAuth signups always have display_name from Google; email signups don't
  // unless they happened to set it. This gates the trading card identity.
  const needsName = !displayName || !displayName.trim()
  const [nameInput, setNameInput] = useState(displayName || '')
  const nameInputTrimmed = nameInput.trim()
  const hasName = !needsName || nameInputTrimmed.length > 0

  const selectedInterests = interests || []
  const denomLabel = denomination || 'your tradition'

  // Compute which section is currently active (visible/interactive).
  // Name section (when required) opens FIRST so the user establishes their
  // identity before tuning preferences.
  const activeSection = useMemo(() => {
    if (editing) return editing
    if (needsName && !nameInputTrimmed) return 'name'
    if (!role) return 'role'
    if (!denomination) return 'denom'
    if (!preferredTranslation) return 'trans'
    return 'interests'
  }, [editing, needsName, nameInputTrimmed, role, denomination, preferredTranslation])

  // Required count: name (if needed) + role + denom + trans. allRequired
  // gates the Continue button.
  const requiredSlots = needsName
    ? [nameInputTrimmed, role, denomination, preferredTranslation]
    : [role, denomination, preferredTranslation]
  const requiredCount = requiredSlots.filter(Boolean).length
  const allRequired = requiredCount === requiredSlots.length
  const roleLabel = roleOptions.find(o => o.value === role)?.label

  function commitName() {
    if (!nameInputTrimmed) return
    onChange({ displayName: nameInputTrimmed })
    setEditing(null)
  }

  const toggleInterest = value => {
    const next = selectedInterests.includes(value)
      ? selectedInterests.filter(v => v !== value)
      : [...selectedInterests, value]
    onChange({ interests: next })
  }

  return (
    <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-5 py-8">
      <style>{`
        @keyframes pdSynFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pdSynRevealUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pd-syn-reveal { animation: pdSynRevealUp 420ms ${EASE}; }

        /* Section card hover — gentle grow + gold border tighten + glow.
           Same treatment as step 6 verse cards for funnel-wide consistency. */
        .pd-syn-section {
          transition: transform 220ms ${EASE}, border-color 220ms ${EASE},
                      box-shadow 240ms ${EASE};
        }
        .pd-syn-section:hover {
          transform: scale(1.005);
          border-color: var(--gold);
          box-shadow: 0 0 28px -10px rgba(212, 168, 67, 0.28);
        }

        /* Close (×) button sits dim by default, brightens on parent hover. */
        .pd-syn-close {
          opacity: 0.45;
          color: var(--text-muted-soft);
          background: transparent;
          transition: opacity 200ms ${EASE}, color 180ms ${EASE},
                      background 180ms ${EASE}, transform 180ms ${EASE};
        }
        .pd-syn-section:hover .pd-syn-close { opacity: 1; }
        .pd-syn-close:hover {
          color: var(--gold);
          background: var(--gold-select);
          transform: scale(1.1);
        }

        /* Summary chip hover — match the section card pattern at smaller scale. */
        .pd-syn-summary {
          transition: transform 200ms ${EASE}, border-color 200ms ${EASE},
                      box-shadow 220ms ${EASE};
        }
        .pd-syn-summary:hover {
          transform: scale(1.01);
          border-color: var(--gold-dim);
          box-shadow: 0 0 20px -10px rgba(212, 168, 67, 0.22);
        }

        /* Interest chip hover — only on unselected chips. Brightens border
           + text + adds a faint gold tint so the cursor target is obvious
           before click. Selected chips already render gold so skip. */
        .pd-syn-chip--off:hover {
          border-color: var(--gold-dim) !important;
          color: var(--text-primary) !important;
          background-color: var(--bg-surface-high) !important;
          box-shadow: 0 0 16px -8px rgba(212, 168, 67, 0.35);
          transform: translateY(-1px);
        }

        /* Dropdown option states — driven by CSS not JS so real cursor
           hover always fires (portaled menu was eating React's synthetic
           onMouseEnter). Three states: rest / selected (subtle gold tint
           + gold text) / hover or keyboard-highlight (gold rail + brighter
           gold tint + gold text). Hover beats selected when both apply. */
        .pd-syn-menu-item {
          color: var(--text-primary);
          background-color: transparent;
        }
        .pd-syn-menu-item.is-selected {
          color: var(--gold);
          background-color: rgba(212, 168, 67, 0.06);
        }
        .pd-syn-menu-item:hover,
        .pd-syn-menu-item.is-kb {
          color: var(--gold);
          background-color: rgba(212, 168, 67, 0.16);
          box-shadow: inset 3px 0 0 var(--gold);
        }

        /* Dropdown option states — driven by CSS not JS so real cursor
           hover always fires (portaled menu was eating React's synthetic
           onMouseEnter). Three states: rest / selected (subtle gold tint
           + gold text) / hover or keyboard-highlight (gold rail + brighter
           gold tint + gold text). Hover beats selected when both apply. */
        .pd-syn-menu-item {
          color: var(--text-primary);
          background-color: transparent;
        }
        .pd-syn-menu-item.is-selected {
          color: var(--gold);
          background-color: rgba(212, 168, 67, 0.06);
        }
        .pd-syn-menu-item:hover,
        .pd-syn-menu-item.is-kb {
          color: var(--gold);
          background-color: rgba(212, 168, 67, 0.16);
          box-shadow: inset 3px 0 0 var(--gold);
        }

        /* Rhythm sliders — cross-browser thumb styling. Track is rendered
           by the divs above the input (gold fill + bg-surface-high rest).
           Input itself is invisible-ish overlay with a styled thumb. */
        .pd-syn-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--gold);
          border: 2px solid var(--bg-base);
          box-shadow: 0 0 0 1px var(--gold-dim), 0 2px 6px rgba(0, 0, 0, 0.35);
          cursor: pointer;
          transition: transform 180ms ${EASE}, box-shadow 180ms ${EASE};
        }
        .pd-syn-slider::-webkit-slider-thumb:hover {
          transform: scale(1.08);
          box-shadow: 0 0 0 1px var(--gold), 0 0 0 6px color-mix(in srgb, var(--gold) 18%, transparent), 0 2px 6px rgba(0,0,0,0.4);
        }
        .pd-syn-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--gold);
          border: 2px solid var(--bg-base);
          box-shadow: 0 0 0 1px var(--gold-dim), 0 2px 6px rgba(0, 0, 0, 0.35);
          cursor: pointer;
        }
        .pd-syn-slider::-webkit-slider-runnable-track { background: transparent; height: 100%; }
        .pd-syn-slider::-moz-range-track { background: transparent; height: 100%; }

        @media (prefers-reduced-motion: reduce) {
          .pd-syn-reveal,
          .pd-syn-section,
          .pd-syn-close,
          .pd-syn-summary,
          .pd-syn-chip,
          .pd-syn-slider::-webkit-slider-thumb { animation: none; transition: none; }
          .pd-syn-section:hover,
          .pd-syn-summary:hover,
          .pd-syn-chip--off:hover { transform: none; }
        }
      `}</style>

      {/* Header — by step 12, welcome has already landed (step 1). The job
          here is to honestly name what's happening: a few more inputs. */}
      <header className="text-center mb-7">
        <h1 className="font-heading text-[1.625rem] sm:text-[1.875rem] leading-[1.25] text-[var(--text-primary)]">
          A few more details.
        </h1>
        <p className="mt-2 text-[14px] text-[var(--text-muted)] max-w-[28rem] mx-auto">
          Each answer tunes how Illuminate talks to you.
        </p>
      </header>

      {/* Progressive reveal stack */}
      <div className="space-y-3">
        {/* 0. NAME — only when displayName is empty. Without this, the
            trading card on the next step reads "Welcome, friend." which
            Mason flagged 2026-05-17. */}
        {needsName ? (
          activeSection === 'name' ? (
            <SectionCard onClose={nameInputTrimmed ? () => setEditing(null) : undefined}>
              <h2 className="font-heading text-lg text-[var(--text-primary)] mb-1">
                What should we call you?
              </h2>
              <p className="text-xs text-[var(--text-muted)] mb-4">
                Shows up on your trading card and anywhere Illuminate greets you.
              </p>
              <input
                type="text"
                value={nameInput}
                onChange={e => setNameInput(e.target.value.slice(0, 60))}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commitName()
                  }
                }}
                onBlur={commitName}
                placeholder="Your first name"
                autoFocus
                className="w-full px-4 py-3 rounded-lg bg-[var(--bg-surface-high)] border border-[var(--glass-border)] text-[var(--text-primary)] text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 focus:border-[var(--gold)]/40"
                aria-label="Your name"
              />
              <button
                type="button"
                onClick={commitName}
                disabled={!nameInputTrimmed}
                className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold transition-colors bg-[var(--gold)] text-[var(--text-on-gold)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Save name
              </button>
            </SectionCard>
          ) : nameInputTrimmed ? (
            <SummaryChip label="Name" value={nameInputTrimmed} onTap={() => setEditing('name')} />
          ) : null
        ) : null}

        {/* 1. ROLE */}
        {activeSection === 'role' ? (
          <SectionCard onClose={role ? () => setEditing(null) : undefined}>
            <h2 className="font-heading text-lg text-[var(--text-primary)] mb-4">
              Which best describes you?
            </h2>
            <Dropdown
              label="Your role"
              value={role}
              placeholder="Select one"
              options={roleOptions}
              onSelect={v => {
                onChange({ role: v })
                setEditing(null)
              }}
            />
          </SectionCard>
        ) : role ? (
          <SummaryChip label="Role" value={roleLabel} onTap={() => setEditing('role')} />
        ) : null}

        {/* 2. DENOMINATION + personalize toggle */}
        {activeSection === 'denom' ? (
          <SectionCard onClose={denomination ? () => setEditing(null) : undefined}>
            <h2 className="font-heading text-lg text-[var(--text-primary)] mb-1">
              Your tradition?
            </h2>
            <p className="text-xs text-[var(--text-muted)] mb-4">
              Helps Illuminate frame answers in language familiar to you.
            </p>
            <Dropdown
              label="Denomination"
              value={denomination}
              placeholder="Select one"
              options={denominationOptions}
              onSelect={v => {
                onChange({ denomination: v })
                setEditing(null)
              }}
            />

            {denomination && (
              <div className="mt-5 flex items-start justify-between gap-4 px-4 py-3.5 bg-[var(--bg-surface-high)] border border-[var(--glass-border)] rounded-md">
                <div className="min-w-0">
                  <label
                    htmlFor="syn-personalize-denom"
                    className="block text-[0.875rem] text-[var(--text-primary)] cursor-pointer"
                  >
                    Tune answers to this tradition
                  </label>
                  <p className="mt-1 text-[12px] leading-relaxed text-[var(--text-muted-soft)]">
                    When relevant, I&rsquo;ll lean into how {denomLabel} commonly teaches a passage.
                  </p>
                </div>
                <Toggle
                  id="syn-personalize-denom"
                  checked={!!personalizeToDenomination}
                  onChange={v => onChange({ personalizeToDenomination: v })}
                />
              </div>
            )}
          </SectionCard>
        ) : denomination ? (
          <SummaryChip label="Tradition" value={denomination} onTap={() => setEditing('denom')} />
        ) : null}

        {/* 3. TRANSLATION */}
        {activeSection === 'trans' ? (
          <SectionCard onClose={preferredTranslation ? () => setEditing(null) : undefined}>
            <h2 className="font-heading text-lg text-[var(--text-primary)] mb-1">
              Preferred Bible translation?
            </h2>
            <p className="text-xs text-[var(--text-muted)] mb-4">
              The default we&rsquo;ll show in chat + Bible Reader.
            </p>
            <Dropdown
              label="Bible translation"
              value={preferredTranslation}
              placeholder="Select one"
              options={translationOptions}
              optionMeta={TRANSLATION_META}
              onSelect={v => {
                onChange({ preferredTranslation: v })
                setEditing(null)
              }}
            />
          </SectionCard>
        ) : preferredTranslation ? (
          <SummaryChip
            label="Translation"
            value={preferredTranslation}
            onTap={() => setEditing('trans')}
          />
        ) : null}

        {/* 4. RHYTHM — 3 sliders (age, days/wk, min/day). Moved here from
            the old standalone step 3 (2026-05-16 restructure). Appears once
            translation is set. Defaults are sensible; user can accept them
            without dragging. Always renders open (no collapse) — sliders are
            the interaction, not a chip selection. */}
        {allRequired ? (
          <SectionCard>
            <h2 className="font-heading text-lg text-[var(--text-primary)] mb-1">
              Your reading rhythm.
            </h2>
            <p className="text-xs text-[var(--text-muted)] mb-5">
              Default values are fine — adjust if you want.
            </p>
            <RhythmSliders age={age} days={days} minutes={minutes} onChange={onChange} />
          </SectionCard>
        ) : null}

        {/* 5. INTERESTS — visible once required complete. Mason's call
            (2026-05-13): collapsible via close button, summary chip shows
            count when collapsed. Tap chip to re-expand. */}
        {allRequired ? (
          interestsCollapsed ? (
            <SummaryChip
              label="Interests"
              value={
                selectedInterests.length > 0
                  ? `${selectedInterests.length} selected`
                  : 'None — tap to add'
              }
              onTap={() => setInterestsCollapsed(false)}
            />
          ) : (
            <SectionCard onClose={() => setInterestsCollapsed(true)}>
              <h2 className="font-heading text-lg text-[var(--text-primary)] mb-1">
                What are you most interested in?
              </h2>
              <p className="text-xs text-[var(--text-muted)] mb-4">
                Pick any. Drives what shows up on your home.
              </p>
              <div className="flex flex-wrap gap-2">
                {interestsOptions.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    selected={selectedInterests.includes(opt.value)}
                    onToggle={() => toggleInterest(opt.value)}
                  />
                ))}
              </div>
            </SectionCard>
          )
        ) : null}
      </div>

      {/* CTA — normal flow, matching every other synthesis step. Was a
          fixed bottom footer; Mason flagged inconsistency with other steps. */}
      <div className="mt-10 flex flex-col items-center gap-4">
        {/* Progress dots — required progress (3 or 4 dots: optional name +
            role + denom + trans). Interests is optional so doesn't count
            toward the gate. */}
        <div className="flex items-center justify-center gap-2" aria-hidden="true">
          {requiredSlots.map((filled, i) => (
            <span
              key={i}
              className={[
                'block h-1.5 rounded-full transition-all',
                filled ? 'w-6 bg-[var(--gold)]' : 'w-1.5 bg-[var(--bg-surface-high)]',
              ].join(' ')}
            />
          ))}
        </div>

        <ContinueButton onClick={onContinue} disabled={!allRequired}>
          {allRequired ? 'Continue' : `Pick ${requiredSlots.length - requiredCount} more`}
        </ContinueButton>
      </div>
    </div>
  )
}
